'use client';

import { Search, X } from 'lucide-react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { searchEventTracker } from '@/lib/analytics/search-tracking';
import { toCanonicalPath } from '@/lib/markets';

interface SearchInputProps {
	defaultValue?: string;
}

export function SearchInput({ defaultValue = '' }: SearchInputProps) {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const [value, setValue] = useState(defaultValue);
	const [, startTransition] = useTransition();
	const inputRef = useRef<HTMLInputElement>(null);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	useEffect(() => {
		setValue(defaultValue);
		searchEventTracker.setInputQuery(defaultValue);
	}, [defaultValue]);

	useEffect(() => {
		return () => {
			searchEventTracker.setInputQuery(null);
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, []);

	const navigateToSearch = useCallback(
		(nextValue: string) => {
			const trimmed = nextValue.trim();
			const currentQuery = (searchParams.get('q') || '').trim();

			if (trimmed === currentQuery) {
				return;
			}

			const params = new URLSearchParams(searchParams.toString());
			if (trimmed) {
				params.set('q', trimmed);
			} else {
				params.delete('q');
			}
			params.delete('after');
			params.delete('before');

			const canonicalPathname = toCanonicalPath(pathname);
			const href = params.toString()
				? `${canonicalPathname}?${params.toString()}`
				: canonicalPathname;

			startTransition(() => {
				router.replace(href, { scroll: false });
			});
		},
		[pathname, router, searchParams],
	);

	const handleSubmit = useCallback(
		(e: React.FormEvent) => {
			e.preventDefault();
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}

			navigateToSearch(value);
		},
		[navigateToSearch, value],
	);

	const handleClear = useCallback(() => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}

		setValue('');
		searchEventTracker.setInputQuery('');
		navigateToSearch('');
	}, [navigateToSearch]);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const nextValue = e.target.value;
			setValue(nextValue);
			searchEventTracker.setInputQuery(nextValue);

			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}

			debounceRef.current = setTimeout(() => {
				navigateToSearch(nextValue);
			}, 250);
		},
		[navigateToSearch],
	);

	return (
		<form onSubmit={handleSubmit}>
			<div className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
				<Search className="h-5 w-5 shrink-0 text-neutral-400" />
				<input
					ref={inputRef}
					type="text"
					value={value}
					onChange={handleChange}
					placeholder="Search products..."
					className="flex-1 bg-transparent text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
					autoComplete="off"
					autoCorrect="off"
					autoCapitalize="off"
					spellCheck={false}
				/>
				{value && (
					<button
						type="button"
						onClick={handleClear}
						className="shrink-0 rounded-full p-0.5 text-neutral-400 transition-colors hover:bg-neutral-200 hover:text-neutral-600"
						aria-label="Clear search"
					>
						<X className="h-4 w-4" />
					</button>
				)}
			</div>
		</form>
	);
}
